<script>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -- nomes de idioma e numeração dos passos ficam fixos; o resto usa i18n */
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import types from 'dashboard/store/mutation-types';

// O QR do WhatsApp gira a cada ~20 s. Renova antes disso enquanto a sessão
// espera a leitura, como o WahaStatusCard faz na tela de reconexão.
const QR_REFRESH_MS = 15000;

// WhatsApp Business wizard backed by WAHA. Creates a Chatwoot Channel::Api
// inbox, then calls our Waha::SessionService endpoints to spin up a WAHA
// session and display the QR code for scanning.
//
// Steps:
//   1. Name the inbox
//   2. Pick agents + advanced config (ignore flags, lock to single conversation)
//   3. QR code + live status polling → on WORKING, redirect to the inbox

export default {
  name: 'WhatsappWebWaha',

  data() {
    return {
      step: 1, // 1=name, 2=agents+config, 3=qr
      inboxName: 'WhatsApp',
      submitting: false,
      errorMessage: '',

      // Agent selection + config
      agents: [],
      agentsLoading: false,
      selectedAgentIds: [],
      lockSingleConversation: true,
      ignoreStatus: true,
      ignoreGroups: false,
      ignoreChannels: true,
      ignoreBroadcast: true,
      showAgentName: true,
      enableCommands: true,
      markAsRead: true,
      locale: 'pt-BR',
      // Os padrões acima servem para a maioria das caixas; o painel fica
      // fechado para o caminho principal ser só nome e quem atende.
      showAdvanced: false,

      // Created inbox (after step 2 submits)
      inboxId: null,

      // QR + status
      qrCode: null,
      qrLoading: false,
      qrError: null,
      qrFetchedAt: 0,
      qrRequestInFlight: false,
      wahaStatus: null, // WORKING | SCAN_QR_CODE | STARTING | STOPPED | FAILED | ERROR | NOT_CONFIGURED
      statusPollTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),

    canContinueStep1() {
      return this.inboxName.trim().length >= 2;
    },

    canCreateInbox() {
      return this.selectedAgentIds.length > 0 && !this.submitting;
    },

    statusLabel() {
      const map = {
        WORKING: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_WORKING'),
        SCAN_QR_CODE: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_SCAN_QR_CODE'),
        STARTING: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_STARTING'),
        STOPPED: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_STOPPED'),
        FAILED: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_FAILED'),
        ERROR: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_ERROR'),
        NOT_CONFIGURED: this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_NOT_CONFIGURED'),
      };
      return map[this.wahaStatus] || this.wahaStatus || this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STATUS_UNKNOWN');
    },

    submitLabel() {
      if (this.submitting) return this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CREATING');
      return this.inboxId
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.RETRY_CONNECT')
        : this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CREATE_AND_CONNECT');
    },

    statusColor() {
      if (this.wahaStatus === 'WORKING') return 'bg-green-500';
      if (this.wahaStatus === 'SCAN_QR_CODE') return 'bg-amber-500';
      if (this.wahaStatus === 'STARTING') return 'bg-blue-500';
      if (this.wahaStatus === 'FAILED' || this.wahaStatus === 'ERROR') return 'bg-red-500';
      return 'bg-n-slate-8';
    },

    statusTextColor() {
      if (this.wahaStatus === 'WORKING') return 'text-green-600 dark:text-green-400';
      if (this.wahaStatus === 'SCAN_QR_CODE') return 'text-amber-600 dark:text-amber-400';
      if (this.wahaStatus === 'FAILED' || this.wahaStatus === 'ERROR') return 'text-red-600 dark:text-red-400';
      return 'text-n-slate-11';
    },
  },

  mounted() {
    this.loadAgents();
  },

  beforeUnmount() {
    this.stopStatusPolling();
  },

  methods: {
    // ── Step 1 → 2 ─────────────────────────────────────────────────────────
    goToStep2() {
      if (!this.canContinueStep1) {
        this.errorMessage = this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NAME_ERROR');
        return;
      }
      this.errorMessage = '';
      this.step = 2;
    },

    // ── Agents ──────────────────────────────────────────────────────────────
    async loadAgents() {
      this.agentsLoading = true;
      try {
        const { data } = await axios.get(`/api/v1/accounts/${this.currentAccountId}/agents`);
        this.agents = data || [];
      } catch (err) {
        this.errorMessage = this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.AGENTS_LOAD_ERROR');
      } finally {
        this.agentsLoading = false;
      }
    },

    toggleAgent(agentId) {
      const idx = this.selectedAgentIds.indexOf(agentId);
      if (idx >= 0) this.selectedAgentIds.splice(idx, 1);
      else this.selectedAgentIds.push(agentId);
    },

    // ── Step 2 → 3 ─────────────────────────────────────────────────────────
    async createInboxAndBootstrap() {
      if (!this.canCreateInbox) return;

      this.submitting = true;
      this.errorMessage = '';
      // Numa nova tentativa depois de falha, a caixa já existe: reaproveita em
      // vez de criar outra caixa sem sessão.
      const reusingInbox = Boolean(this.inboxId);

      try {
        // 1. Create the Chatwoot inbox as Channel::Api so the public API
        //    (used by WAHA's chatwoot integration to create contacts) finds
        //    the inbox via its polymorphic channel_type column. Creating with
        //    'whatsapp_web' would set channel_type=Channel::WhatsappWeb, and
        //    Public::Api::V1::InboxesController does Channel::Api.find_by!,
        //    so the .inbox association comes back nil and WAHA gets a 500.
        if (!reusingInbox) {
          const apiInbox = await this.$store.dispatch('inboxes/createChannel', {
            name: this.inboxName.trim(),
            channel: {
              type: 'api',
              additional_attributes: {},
            },
          });
          this.inboxId = apiInbox.id;
        }
        const inboxId = this.inboxId;

        // 2. Add selected agents to the inbox
        if (this.selectedAgentIds.length > 0) {
          await axios.post(`/api/v1/accounts/${this.currentAccountId}/inbox_members`, {
            inbox_id: inboxId,
            user_ids: this.selectedAgentIds,
          });
        }

        // 3. Bootstrap WAHA session via our new controller
        const { data: wahaData } = await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${inboxId}/waha/create_session`,
          {
            engine: 'GOWS',
            ignore_status: this.ignoreStatus,
            ignore_groups: this.ignoreGroups,
            ignore_channels: this.ignoreChannels,
            ignore_broadcast: this.ignoreBroadcast,
            lock_single_conversation: this.lockSingleConversation,
            show_agent_name: this.showAgentName,
            enable_commands: this.enableCommands,
            mark_as_read: this.markAsRead,
            locale: this.locale,
          }
        );

        if (wahaData?.session_name) {
          this.$store.commit(`inboxes/${types.UPDATE_WAHA_STATUS}`, {
            inboxId,
            wahaSessionName: wahaData.session_name,
            wahaStatus: wahaData.status,
            wahaStatusCheckedAt: wahaData.checked_at,
          });
        }

        // 4. Advance to QR step and start polling
        this.step = 3;
        this.startStatusPolling();
        this.fetchQrCode();
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.UNKNOWN_ERROR');
        this.errorMessage = this.inboxId
          ? this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CONNECT_ERROR', { message })
          : this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CREATE_ERROR', { message });
        useAlert(this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    // ── QR polling ──────────────────────────────────────────────────────────
    async fetchQrCode() {
      if (!this.inboxId || this.qrRequestInFlight) return;
      this.qrRequestInFlight = true;
      // Renovação com QR na tela troca a imagem sem piscar o spinner.
      const refreshing = Boolean(this.qrCode);
      if (!refreshing) this.qrLoading = true;
      this.qrError = null;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/qr_code`
        );
        this.qrCode = data.qr_code || null;
        this.qrFetchedAt = Date.now();
        if (data.status) this.wahaStatus = data.status;
      } catch (err) {
        // Falha numa renovação mantém o QR atual; o próximo ciclo tenta de novo.
        if (!refreshing) {
          this.qrError = this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.QR_ERROR');
        }
      } finally {
        this.qrLoading = false;
        this.qrRequestInFlight = false;
      }
    },

    shouldRefreshQr() {
      return !this.qrCode || Date.now() - this.qrFetchedAt >= QR_REFRESH_MS;
    },

    startStatusPolling() {
      if (this.statusPollTimer) clearInterval(this.statusPollTimer);
      this.statusPollTimer = setInterval(() => this.pollStatus(), 3000);
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },

    async pollStatus() {
      if (!this.inboxId) return;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/status`
        );
        this.wahaStatus = data.status;

        if (data.status === 'WORKING') {
          this.stopStatusPolling();
          this.onConnected();
        } else if (data.status === 'SCAN_QR_CODE' && this.shouldRefreshQr()) {
          this.fetchQrCode();
        }
      } catch (err) {
        // silent, keep polling
      }
    },

    async reconnect() {
      if (!this.inboxId) return;
      try {
        this.qrCode = null;
        this.qrLoading = true;
        this.wahaStatus = 'STARTING';
        await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/reconnect`
        );
        setTimeout(() => this.fetchQrCode(), 2000);
      } catch (err) {
        this.qrError = this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.RECONNECT_ERROR');
      } finally {
        this.qrLoading = false;
      }
    },

    onConnected() {
      useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CONNECTED_ALERT'));
      setTimeout(() => {
        router.replace({
          name: 'settings_inbox_show',
          params: {
            accountId: this.currentAccountId,
            inboxId: this.inboxId,
          },
        });
      }, 2000);
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-auto">
<!-- ═════════════ Step 1: Name ═════════════ -->
    <template v-if="step === 1">
      <div class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_WEB.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-2">
          {{ $t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_WEB.DESCRIPTION') }}
        </p>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP1_DESC') }}
        </p>

        <div class="mb-4">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NAME_LABEL') }}
          </label>
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NAME_PLACEHOLDER')"
            class="w-full px-4 py-2.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
            @keyup.enter="goToStep2"
          />
          <p class="text-xs text-n-slate-11 mt-1.5">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NAME_HELP') }}
          </p>
        </div>

        <div
          v-if="errorMessage"
          class="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-5 py-2 rounded-xl text-sm font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
            :disabled="!canContinueStep1"
            @click="goToStep2"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NEXT') }} →
          </button>
        </div>
      </div>
    </template>

    <!-- ═════════════ Step 2: Agents + Config ═════════════ -->
    <template v-if="step === 2">
      <div class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP2_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP2_DESC') }} <strong>{{ inboxName }}</strong>.
        </p>

        <!-- Agents list -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.AGENTS_LABEL') }} <span class="text-red-500">*</span>
          </label>
          <div
            class="max-h-72 overflow-y-auto border border-n-weak rounded-xl p-3 space-y-1 bg-n-surface-1"
          >
            <div v-if="agentsLoading" class="text-center py-4 text-n-slate-11 text-sm">
              <span class="i-ri-loader-4-line size-4 animate-spin inline-block" />
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.LOADING') }}
            </div>
            <div v-else-if="!agents.length" class="text-center py-4 text-n-slate-11 text-sm">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NO_AGENTS') }}
            </div>
            <label
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center gap-3 p-2 rounded hover:bg-n-slate-2 dark:hover:bg-n-solid-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedAgentIds.includes(agent.id)"
                class="size-4 accent-n-blue-9"
                @change="toggleAgent(agent.id)"
              />
              <div class="size-8 rounded-full bg-n-slate-3 dark:bg-n-solid-3 flex items-center justify-center text-xs font-bold text-n-slate-12 overflow-hidden">
                <img v-if="agent.thumbnail" :src="agent.thumbnail" class="w-full h-full object-cover" />
                <template v-else>{{ (agent.name[0] || '?').toUpperCase() }}</template>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-n-slate-12 truncate font-medium">{{ agent.name }}</div>
                <div class="text-xs text-n-slate-11 truncate">{{ agent.email }}</div>
              </div>
            </label>
          </div>
        </div>

        <!-- Config: opções avançadas recolhidas, com os padrões atuais -->
        <button
          type="button"
          class="flex items-center gap-2 min-h-11 mb-2 px-1 rounded-lg text-sm font-bold text-n-slate-12 hover:text-n-blue-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :aria-expanded="showAdvanced"
          aria-controls="waha-wizard-advanced"
          @click="showAdvanced = !showAdvanced"
        >
          <span
            class="size-4 transition-transform i-ph-caret-right"
            :class="{ 'rotate-90': showAdvanced }"
            aria-hidden="true"
          />
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.ADVANCED_TITLE') }}
        </button>
        <p v-if="!showAdvanced" class="text-xs text-n-slate-11 mb-6 px-1">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.ADVANCED_HINT') }}
        </p>
        <div v-show="showAdvanced" id="waha-wizard-advanced" class="space-y-2 mb-6">
          <!-- NEW: show agent name -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.SHOW_AGENT_NAME_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.SHOW_AGENT_NAME_DESC') }}</div>
            </div>
            <input v-model="showAgentName" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: enable commands -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.ENABLE_COMMANDS_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.ENABLE_COMMANDS_DESC') }}</div>
            </div>
            <input v-model="enableCommands" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: mark as read -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.MARK_AS_READ_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.MARK_AS_READ_DESC') }}</div>
            </div>
            <input v-model="markAsRead" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: locale -->
          <div class="p-3 rounded-xl border border-n-weak bg-n-surface-1">
            <div class="flex items-center justify-between gap-3">
              <div>
                <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.LOCALE_TITLE') }}</div>
                <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.LOCALE_DESC') }}</div>
              </div>
              <select
                v-model="locale"
                class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
              >
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English (US)</option>
              </select>
            </div>
          </div>

          <div class="pt-2 border-t border-n-weak" />

          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.LOCK_SINGLE_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.LOCK_SINGLE_DESC') }}</div>
            </div>
            <input v-model="lockSingleConversation" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_STATUS_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_STATUS_DESC') }}</div>
            </div>
            <input v-model="ignoreStatus" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_GROUPS_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_GROUPS_DESC') }}</div>
            </div>
            <input v-model="ignoreGroups" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_CHANNELS_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_CHANNELS_DESC') }}</div>
            </div>
            <input v-model="ignoreChannels" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_BROADCAST_TITLE') }}</div>
              <div class="text-xs text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.IGNORE_BROADCAST_DESC') }}</div>
            </div>
            <input v-model="ignoreBroadcast" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
        </div>

        <div
          v-if="errorMessage"
          class="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>

        <div class="flex justify-between gap-2">
          <button
            class="px-4 py-2 rounded-xl text-sm text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
            :disabled="submitting || !!inboxId"
            @click="step = 1"
          >
            ← {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.BACK') }}
          </button>
          <button
            class="px-5 py-2 rounded-xl text-sm font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
            :disabled="!canCreateInbox"
            @click="createInboxAndBootstrap"
          >
            <span v-if="submitting" class="i-ri-loader-4-line size-4 animate-spin inline-block mr-1" />
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </template>

    <!-- ═════════════ Step 3: QR Code + Status ═════════════ -->
    <template v-if="step === 3">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-2xl font-medium text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP3_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP3_DESC_PREFIX') }} <strong>{{ inboxName }}</strong> {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP3_DESC_SUFFIX') }}
        </p>
        <p class="text-xs text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.QR_SETTINGS_HINT') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Instructions -->
          <div class="space-y-4">
            <h3 class="text-sm font-bold text-n-slate-12">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.HOW_TO_TITLE') }}</h3>
            <ol class="space-y-3">
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">1</span>
                <span class="text-sm text-n-slate-12 pt-0.5">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.HOW_TO_STEP_1') }}</span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">2</span>
                <span class="text-sm text-n-slate-12 pt-0.5">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.HOW_TO_STEP_2') }}</span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">3</span>
                <span class="text-sm text-n-slate-12 pt-0.5">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.HOW_TO_STEP_3') }}</span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">4</span>
                <span class="text-sm text-n-slate-12 pt-0.5">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.HOW_TO_STEP_4') }}</span>
              </li>
            </ol>

            <div class="p-3 rounded-xl border border-n-weak bg-n-surface-1 flex items-center gap-3">
              <div class="size-3 rounded-full animate-pulse" :class="statusColor" />
              <span class="text-sm font-medium" :class="statusTextColor">{{ statusLabel }}</span>
            </div>
          </div>

          <!-- QR code panel -->
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-6 flex flex-col items-center justify-center min-h-[360px]">
            <div v-if="qrError" class="text-center">
              <span class="i-ri-wifi-off-line size-12 text-red-500 block mx-auto mb-3" />
              <p class="text-sm text-red-600 dark:text-red-400 mb-4">{{ qrError }}</p>
              <button
                class="px-4 py-2 rounded-xl text-xs font-semibold bg-n-blue-9 text-white"
                @click="reconnect"
              >
                {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.RETRY_RECONNECT') }}
              </button>
            </div>

            <div v-else-if="wahaStatus === 'WORKING'" class="text-center">
              <div class="size-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span class="i-ri-check-line size-12 text-white" />
              </div>
              <p class="text-xl font-medium text-green-600 dark:text-green-400 mb-1">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.CONNECTED_TITLE') }}</p>
              <p class="text-sm text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.REDIRECTING') }}</p>
            </div>

            <div v-else-if="qrLoading || !qrCode" class="text-center">
              <span class="i-ri-loader-4-line size-12 text-n-blue-9 animate-spin block mx-auto mb-3" />
              <p class="text-sm text-n-slate-11">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.GENERATING_QR') }}</p>
            </div>

            <div v-else class="flex flex-col items-center gap-4">
              <img :src="qrCode" :alt="$t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.QR_ALT')" class="w-64 h-64 rounded-2xl border-4 border-n-weak" />
              <p class="text-xs text-n-slate-11 text-center">{{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.QR_AUTO_REFRESH') }}</p>
              <button
                class="px-3 py-1.5 rounded-xl text-xs font-medium text-n-slate-11 border border-n-weak hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
                @click="reconnect"
              >
                <span class="i-ri-refresh-line size-3 inline-block mr-1" />
                {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.NEW_QR') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
</div>
</template>
